export const ALL_POSITIONS = ["QB", "RB", "WR", "TE", "DST"];

const PARAM_KEYS = {
  season: "season",
  positions: "pos",
  weeks: "weeks",
  teams: "teams",
};

const byNumber = (a, b) => a - b;

function toSearchString(params) {
  const str = params.toString().replace(/%2C/gi, ",");
  return str ? `?${str}` : "";
}

function parseList(value) {
  if (!value) {
    return [];
  }
  const items = value
    .split(",")
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
  return [...new Set(items)];
}

export function parseWeeks(value, maxWeek = 18) {
  if (!value) {
    return [];
  }
  const weeks = new Set();
  value.split(",").forEach((part) => {
    const match = part.trim().match(/^(\d+)(?:-(\d+))?$/);
    if (!match) {
      return;
    }
    let start = Number(match[1]);
    let end = match[2] ? Number(match[2]) : start;
    if (start > end) {
      [start, end] = [end, start];
    }
    for (let week = start; week <= end; week++) {
      if (week >= 1 && week <= maxWeek) {
        weeks.add(week);
      }
    }
  });
  return [...weeks].sort(byNumber);
}

export function formatWeeks(weeks) {
  const sorted = [...new Set(weeks)].sort(byNumber);
  const parts = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) {
      j++;
    }
    parts.push(i === j ? `${sorted[i]}` : `${sorted[i]}-${sorted[j]}`);
    i = j + 1;
  }
  return parts.join(",");
}

export function weeksLabel(weeks) {
  const text = formatWeeks(weeks).replace(/-/g, "–").replace(/,/g, ", ");
  return weeks.length === 1 ? `Week ${text}` : `Weeks ${text}`;
}

export function weekRange(a, b) {
  const start = Math.min(a, b);
  const end = Math.max(a, b);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function toggleWeek(weeks, week, { anchor = null } = {}) {
  if (anchor !== null && anchor !== undefined) {
    return [...new Set([...weeks, ...weekRange(anchor, week)])].sort(byNumber);
  }
  if (weeks.includes(week)) {
    return weeks.filter((w) => w !== week);
  }
  return [...weeks, week].sort(byNumber);
}

export function sameWeeks(a, b) {
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort(byNumber);
  const sortedB = [...b].sort(byNumber);
  return sortedA.every((week, i) => week === sortedB[i]);
}

export function toggleValue(list, value) {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
}

export function parseFilters(
  search,
  { positions = ALL_POSITIONS, maxWeek = 18 } = {}
) {
  const params = new URLSearchParams(search);
  const season = Number(params.get(PARAM_KEYS.season));
  const weeks = parseWeeks(params.get(PARAM_KEYS.weeks), maxWeek);

  return {
    season: Number.isInteger(season) && season > 1900 ? season : null,
    positions: parseList(params.get(PARAM_KEYS.positions)).filter((pos) =>
      positions.includes(pos)
    ),
    weeks: weeks.length > 0 ? weeks : null,
    teams: parseList(params.get(PARAM_KEYS.teams)).filter((team) =>
      /^[A-Z]{2,3}$/.test(team)
    ),
  };
}

function encodeField(field, value) {
  if (value === null || value === undefined) {
    return "";
  }
  if (field === "season") {
    return String(value);
  }
  if (field === "weeks") {
    return value.length ? formatWeeks(value) : "";
  }
  return value.length ? value.join(",") : "";
}

export function updateSearch(search, patch) {
  const params = new URLSearchParams(search);
  Object.keys(PARAM_KEYS).forEach((field) => {
    if (!(field in patch)) {
      return;
    }
    const encoded = encodeField(field, patch[field]);
    if (encoded) {
      params.set(PARAM_KEYS[field], encoded);
    } else {
      params.delete(PARAM_KEYS[field]);
    }
  });
  return toSearchString(params);
}

export function carryParams(search) {
  const params = new URLSearchParams(search);
  const carried = new URLSearchParams();
  [PARAM_KEYS.season, PARAM_KEYS.teams].forEach((key) => {
    if (params.get(key)) {
      carried.set(key, params.get(key));
    }
  });
  return toSearchString(carried);
}

export function buildReference(data) {
  const seasons = (data.nflStatSeasons || [])
    .map((season) => {
      const nodes = season.nflstatweekSet.edges
        .map((edge) => edge.node)
        .filter(Boolean);
      const weeks = nodes.map((node) => node.week).sort(byNumber);
      const currentNode = nodes.find((node) => node.current);
      let playedThrough = weeks.length ? weeks[weeks.length - 1] : 0;
      if (season.current) {
        playedThrough = currentNode ? currentNode.week : 0;
      }
      return {
        id: season.id,
        year: season.year,
        current: season.current,
        weeks,
        playedThrough,
        currentWeek: currentNode ? currentNode.week : null,
      };
    })
    .sort((a, b) => b.year - a.year);

  return {
    groups: data.whoAmI ? data.whoAmI.groups : null,
    username: data.whoAmI ? data.whoAmI.username : null,
    seasons,
    currentSeason: seasons.find((season) => season.current) || seasons[0] || null,
    teams: data.nflStatTeams || [],
  };
}

export function seasonFor(year, reference) {
  if (year) {
    const match = reference.seasons.find((season) => season.year === year);
    if (match) {
      return match;
    }
  }
  return reference.currentSeason;
}

export function playedWeeks(season) {
  if (!season) {
    return [];
  }
  return season.weeks.filter((week) => week <= season.playedThrough);
}

export function resolveTeamIds(abbrevs, teams) {
  return abbrevs
    .map((abbrev) => teams.find((team) => team.abbreviation === abbrev))
    .filter(Boolean)
    .map((team) => team.id);
}
